import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Contact = () => {
  return (
    <section id="contact" className="py-24 bg-gray-50 relative overflow-hidden">
      <motion.div
        className="absolute top-0 left-0 w-64 h-64 bg-brand-accent/5 rounded-full blur-3xl pointer-events-none"
        animate={{ x: [0, 40, 0], y: [0, 30, 0] }}
        transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute bottom-0 right-0 w-96 h-96 bg-brand-gradEnd/5 rounded-full blur-3xl pointer-events-none"
        animate={{ x: [0, -30, 0], y: [0, -20, 0] }}
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
      />

      <div className="container mx-auto px-6 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, type: 'spring', stiffness: 80 }}
          >
            <h2 className="text-sm font-bold text-brand-accent uppercase tracking-widest mb-2">Get In Touch</h2>
            <h3 className="text-3xl md:text-5xl font-extrabold text-brand-secondary mb-6">
              Ready to Take the Next Step in Your Career?
            </h3>
            <p className="text-gray-600 text-lg mb-10">
              Reach out today and let's build a professional brand that gets you noticed, respected, and hired.
            </p>

            <div className="space-y-6">
              {[
                { icon: FiIcons.FiMail, label: 'Email Us', value: 'contact@revivaltechfirm.com', href: 'mailto:contact@revivaltechfirm.com' },
                { icon: FiIcons.FiPhone, label: 'Call Us', value: '(832) 299-4360', href: 'tel:+18322994360' },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  className="flex items-start"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + i * 0.15 }}
                  whileHover={{ x: 6 }}
                >
                  <motion.div
                    className="w-12 h-12 rounded-full bg-white shadow-md flex items-center justify-center text-brand-accent mr-6 flex-shrink-0"
                    whileHover={{ scale: 1.15, backgroundColor: '#021351', color: '#3BDFF8' }}
                    transition={{ duration: 0.3 }}
                  >
                    <SafeIcon icon={item.icon} className="text-xl" />
                  </motion.div>
                  <div>
                    <h4 className="text-sm text-gray-500 uppercase tracking-wider font-bold mb-1">{item.label}</h4>
                    <a href={item.href} className="text-xl font-semibold text-brand-secondary hover:text-brand-accent transition-colors">
                      {item.value}
                    </a>
                  </div>
                </motion.div>
              ))}

              {/* LinkedIn Card */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
                whileHover={{ x: 6 }}
              >
                <a
                  href="https://www.linkedin.com/in/garyfagerholm/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start group"
                >
                  <motion.div
                    className="w-12 h-12 rounded-full bg-white shadow-md flex items-center justify-center text-[#0077B5] mr-6 flex-shrink-0"
                    whileHover={{ scale: 1.15, backgroundColor: '#0077B5', color: '#ffffff' }}
                    transition={{ duration: 0.3 }}
                  >
                    <FiIcons.FiLinkedin className="text-xl" />
                  </motion.div>
                  <div className="flex-1">
                    <h4 className="text-sm text-gray-500 uppercase tracking-wider font-bold mb-1">Connect on LinkedIn</h4>
                    <p className="text-xl font-semibold text-brand-secondary group-hover:text-[#0077B5] transition-colors">
                      Gary Fagerholm
                    </p>
                    <p className="text-sm text-gray-400 mt-0.5">Message me directly or view my profile</p>
                  </div>
                  <motion.div
                    className="self-center opacity-0 group-hover:opacity-100 transition-opacity"
                    initial={{ x: -5 }}
                    whileHover={{ x: 0 }}
                  >
                    <SafeIcon icon={FiIcons.FiExternalLink} className="text-[#0077B5] text-sm" />
                  </motion.div>
                </a>
              </motion.div>
            </div>

            {/* LinkedIn Profile Preview Card */}
            <motion.a
              href="https://www.linkedin.com/in/garyfagerholm/"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.65 }}
              whileHover={{ y: -4, boxShadow: '0 12px 40px rgba(0,119,181,0.18)' }}
              className="mt-8 flex items-center gap-4 bg-white border border-[#0077B5]/20 rounded-2xl p-4 shadow-md cursor-pointer group block"
            >
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#0077B5] to-[#00a0dc] flex items-center justify-center flex-shrink-0 shadow">
                <span className="text-white font-extrabold text-lg">GF</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-brand-secondary group-hover:text-[#0077B5] transition-colors">Gary Fagerholm</p>
                <p className="text-sm text-gray-500 truncate">Co-Founder · Revival Tech Firm</p>
                <div className="flex items-center gap-1.5 mt-1">
                  <div className="w-2 h-2 rounded-full bg-[#0077B5]" />
                  <span className="text-xs text-[#0077B5] font-semibold">View LinkedIn Profile</span>
                </div>
              </div>
              <div className="w-8 h-8 rounded-full bg-[#0077B5]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#0077B5] transition-colors">
                <FiIcons.FiLinkedin className="text-[#0077B5] group-hover:text-white transition-colors text-sm" />
              </div>
            </motion.a>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.75 }}
              className="flex items-center gap-8 mt-10 flex-wrap"
            >
              {[
                { value: '60%', label: 'Avg. Profile Boost' },
                { value: '100%', label: 'Client Satisfaction' },
                { value: '5★', label: 'Google Rating' },
              ].map((badge, i) => (
                <div key={i} className="text-center">
                  <p className="text-2xl font-extrabold text-brand-secondary">{badge.value}</p>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">{badge.label}</p>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* FormRobin Embed */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden"
          >
            <iframe
              src="https://formrobin.com/f/3zk5qr3"
              title="Contact Form"
              width="100%"
              height="620"
              frameBorder="0"
              style={{ display: 'block', border: 'none' }}
              loading="lazy"
              allow="clipboard-write"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;